import React, { useRef, useState, useEffect } from 'react';
import { Star, ChevronDown, Search, ChevronUp } from 'lucide-react';
import styles from "../../_css/ReportingAndSterring.module.css";
import { useClickOutsideWithCallback } from "../hooks/useClickOutside.js";
import { useDashboardFilters } from "./context/DashboardContext.js";
import { getCompaniesList, getCustomizedRoleplays } from "../../requests.js"
import ResponseLoader from "../common/ResponseLoader.js"
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js"
import { useDispatch } from "react-redux";

const CustomizedRolePlays = () => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOption, setSelectedOption] = useState('Top 3');
    const [allCompanies, setAllCompanies] = useState([]);
    const [rolePlaysData, setRolePlaysData] = useState({ companies: [], top3: [] });
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const { filters } = useDashboardFilters();
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);

    useClickOutsideWithCallback(dropdownRef, () => {
        setShowDropdown(false);
    });

    // Check if global filter has a specific company selected (not 'all')
    const hasGlobalCompanyFilter = filters.company && filters.company !== 'all';

    // Fetch all companies on component mount
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setLoading(true);
                dispatch(startLoader());
                const companies = await getCompaniesList();
                setAllCompanies(companies.result);
            } catch (err) {
                console.error('Error fetching companies:', err);
            } finally {
                setLoading(false);
                dispatch(stopLoader());
            }
        };

        fetchCompanies();
    }, [dispatch]);

    // Fetch role plays data when filters or selected option changes
    useEffect(() => {
        const fetchRolePlaysData = async () => {
            try {
                setDataLoading(true);
                dispatch(startLoader());

                const startDate = filters.dateRange.from;
                const endDate = filters.dateRange.to;
                const companyName = filters.company;
                const data = await getCustomizedRoleplays({
                    startDate,
                    endDate,
                    companyName
                });
                // Handle the API response structure properly
                if (data && data.result) {
                    setRolePlaysData({
                        companies: data.result.companies || [],
                        top3: data.result.top3 || []
                    });
                } else {
                    setRolePlaysData({ companies: [], top3: [] });
                }
            } catch (err) {
                console.error('Error fetching role plays data:', err);
                setRolePlaysData({ companies: [], top3: [] });
            } finally {
                setDataLoading(false);
                dispatch(stopLoader());
            }
        };

        fetchRolePlaysData();
    }, [filters, dispatch]);

    const toggleDropdown = () => {
        if (!hasGlobalCompanyFilter) {
            setShowDropdown(!showDropdown);
        }
    };

    // Get unique company names from rolePlaysData for dropdown
    const getAvailableCompanies = () => {
        const companiesFromData = rolePlaysData.companies.map(item => item.company);
        return [...new Set(companiesFromData)]; // Remove duplicates
    };

    // Get companies to display in dropdown based on search
    const getDropdownCompanies = () => {
        const availableCompanies = getAvailableCompanies();
        if (searchTerm) {
            return availableCompanies.filter(company =>
                company && company.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return availableCompanies;
    };

    // Get companies to display in table based on selection and global filters
    const getDisplayCompanies = () => {
        // If global filter has a specific company, show only that company's data
        if (hasGlobalCompanyFilter) {
            return rolePlaysData.companies.filter(item => item.company === filters.company);
        }

        // Otherwise, use the component's local filter
        if (selectedOption === 'Top 3') {
            // Use the top3 array from API response if available
            return rolePlaysData.top3.length > 0 ? rolePlaysData.top3 : rolePlaysData.companies.slice(0, 3);
        } else {
            // Filter by selected company
            return rolePlaysData.companies.filter(item => item.company === selectedOption);
        }
    };

    const handleOptionSelect = (option) => {
        if (!hasGlobalCompanyFilter) {
            setSelectedOption(option);
            setSearchTerm('');
            setShowDropdown(false);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString || dateString === 'No data') return 'No data';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Get display text for dropdown button
    const getDropdownButtonText = () => {
        if (hasGlobalCompanyFilter) {
            return filters.company; // Show the globally selected company
        }
        return selectedOption;
    };

    const dropdownCompanies = getDropdownCompanies();
    const displayCompanies = getDisplayCompanies();

    // Check if we have any data to display
    const hasData = rolePlaysData.companies.length > 0 || rolePlaysData.top3.length > 0;

    // Show loading state
    if (dataLoading) {
        return (
            <div className={styles.rolePlaysContainer}>
                <div className="card-body">
                    <div className={styles.rolePlaysHeader}>
                        <h5 className={`mb-3 d-flex align-items-center ${styles.rolePlaysTitle}`}>
                            Customized Role Plays
                        </h5>
                        <div className={styles.dropdownWrapper} ref={dropdownRef}>
                            <button
                                onClick={toggleDropdown}
                                className={styles.dropdownButton}
                                disabled={loading || dataLoading || hasGlobalCompanyFilter}
                            >
                                {loading ? 'Loading...' : getDropdownButtonText()}
                                {!hasGlobalCompanyFilter && (
                                    showDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className={styles.loadingContainer}>
                        <ResponseLoader />
                    </div>
                </div>
            </div>
        );
    }

    // Show no data state
    if (!hasData && !dataLoading) {
        return (
            <div className={styles.rolePlaysContainer}>
                <div className="card-body">
                    <div className={styles.rolePlaysHeader}>
                        <h5 className={`mb-3 d-flex align-items-center ${styles.rolePlaysTitle}`}>
                            Customized Role Plays

                        </h5>
                        <div className={styles.dropdownWrapper} ref={dropdownRef}>
                            <button
                                onClick={toggleDropdown}
                                className={styles.dropdownButton}
                                disabled={loading || !hasData || hasGlobalCompanyFilter}
                            >
                                {loading ? 'Loading...' : getDropdownButtonText()}
                                {!hasGlobalCompanyFilter && (
                                    showDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />
                                )}
                            </button>

                            {showDropdown && !hasGlobalCompanyFilter && (
                                <div className={styles.dropdownMenu}>
                                    <div className={styles.searchWrapper}>
                                        <input
                                            type="text"
                                            placeholder="Search companies..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                        <Search
                                            size={16}
                                            color="#6B7280"
                                            className={styles.searchIcon}
                                        />
                                    </div>

                                    <div className={styles.dropdownOptions}>
                                        <div
                                            onMouseDown={() => handleOptionSelect('Top 3')}
                                            className={`${styles.dropdownOption} ${selectedOption === 'Top 3' ? styles.activeOption : ''}`}
                                        >
                                            Top 3
                                        </div>
                                        {dropdownCompanies.map((company) => (
                                            <div
                                                key={company}
                                                onMouseDown={() => handleOptionSelect(company)}
                                                className={`${styles.dropdownOption} ${selectedOption === company ? styles.activeOption : ''}`}
                                            >
                                                {company}
                                            </div>
                                        ))}

                                        {searchTerm && dropdownCompanies.length === 0 && (
                                            <div className={styles.noResults}>
                                                No companies found for "{searchTerm}"
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.noDataContainer}>
                        <div className={styles.noDataMessage}>
                            No data to preview
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.rolePlaysContainer}>
            <div className="card-body">
                <div className={styles.rolePlaysHeader}>
                    <h5 className={`mb-3 d-flex align-items-center ${styles.rolePlaysTitle}`}>
                        Customized Role Plays
                    </h5>
                    <div className={styles.dropdownWrapper} ref={dropdownRef}>
                        <button
                            onClick={toggleDropdown}
                            className={styles.dropdownButton}
                            disabled={loading || !hasData || hasGlobalCompanyFilter}
                        >
                            {loading ? 'Loading...' : getDropdownButtonText()}
                            {!hasGlobalCompanyFilter && (
                                showDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />
                            )}
                        </button>

                        {showDropdown && !hasGlobalCompanyFilter && (
                            <div className={styles.dropdownMenu}>
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="text"
                                        placeholder="Search companies..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                    <Search
                                        size={16}
                                        color="#6B7280"
                                        className={styles.searchIcon}
                                    />
                                </div>

                                <div className={styles.dropdownOptions}>
                                    <div
                                        onMouseDown={() => handleOptionSelect('Top 3')}
                                        className={`${styles.dropdownOption} ${selectedOption === 'Top 3' ? styles.activeOption : ''}`}
                                    >
                                        Top 3
                                    </div>
                                    {dropdownCompanies.map((company) => (
                                        <div
                                            key={company}
                                            onMouseDown={() => handleOptionSelect(company)}
                                            className={`${styles.dropdownOption} ${selectedOption === company ? styles.activeOption : ''}`}
                                        >
                                            {company}
                                        </div>
                                    ))}

                                    {searchTerm && dropdownCompanies.length === 0 && (
                                        <div className={styles.noResults}>
                                            No companies found for "{searchTerm}"
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <table className={`table table-borderless align-middle ${styles.rolePlaysTable}`}>
                    <thead className={styles.tableHead}>
                        <tr>
                            <th className={styles.tableHeader}>Company</th>
                            <th className={styles.tableHeader}>Total Published</th>
                            <th className={styles.tableHeader}>Last Published</th>
                            <th className={styles.tableHeader}>This vs Last Month</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayCompanies.map((companyData, index) => (
                            <tr key={`${companyData.company}-${index}`}>
                                <td className={styles.tableCell}>{companyData.company}</td>
                                <td className={styles.tableCell}>
                                    {companyData.totalPublished || 0}
                                </td>
                                <td className={styles.tableCell}>
                                    {formatDate(companyData.lastPublished)}
                                </td>
                                <td className={styles.trendCell}>
                                    {companyData.trend > 0 ? (
                                        <>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/img/upward2.png`}
                                                alt="Upward Trend"
                                                className={styles.trendIcon}
                                            />
                                            +{companyData.trend}%
                                        </>
                                    ) : companyData.trend < 0 ? (
                                        <>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/img/downward2.png`}
                                                alt="Downward Trend"
                                                className={styles.trendIcon}
                                            />
                                            {companyData.trend}%
                                        </>
                                    ) : (
                                        <>
                                            <span className={styles.neutralTrend}>➖</span>
                                            {companyData.trend || 0}%
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomizedRolePlays;